/**
 * Authentication Service
 * Handles login, logout, and current user fetching
 * Uses httpOnly cookies for secure token storage (not vulnerable to XSS)
 */

import { api } from './api';
import type { LoginRequest, LoginResponse, User } from '@/types/auth';

// Track auth state in memory (cookie is the source of truth, but we cache the result)
let isAuthenticatedCache: boolean | null = null;

export const authService = {
  /**
   * Login with username and password or PIN
   * Token is automatically stored in httpOnly cookie by the server
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    isAuthenticatedCache = true;
    return response;
  },

  /**
   * Logout - server clears the httpOnly cookie
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      isAuthenticatedCache = false;
    }
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<{ user: User }>('/auth/me');
    isAuthenticatedCache = true;
    return response.user;
  },

  /**
   * Check if user is authenticated
   * Makes an API call to verify (cookie is httpOnly, can't check client-side)
   */
  async checkAuth(): Promise<boolean> {
    if (isAuthenticatedCache !== null) {
      return isAuthenticatedCache;
    }
    try {
      await api.get('/auth/me');
      isAuthenticatedCache = true;
      return true;
    } catch {
      isAuthenticatedCache = false;
      return false;
    }
  },

  /**
   * Synchronous auth check using cached value
   * Use checkAuth() for accurate check, this is for quick UI decisions
   */
  isAuthenticated(): boolean {
    return isAuthenticatedCache === true;
  },

  /**
   * Clear the cached auth state (used when 401 is received)
   */
  clearAuthCache(): void {
    isAuthenticatedCache = false;
  },
};

/**
 * Authenticated fetch wrapper
 * Automatically includes credentials (httpOnly cookie)
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);

  // Only set Content-Type if not already set and not FormData
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, {
    ...options,
    credentials: 'include',  // Send httpOnly cookies
    headers,
  });
}
